// src/components/CourseList.js
"use client"

import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import ReactDOM from 'react-dom';
import { useFuncion } from "../context/FuncionContext"
import LoadingSpinner from "../components/LoadingSpinner"
import cacheManager from "../utils/cacheManager"
import { useAuth } from "../context/AuthContext"
import { useNavigate } from "react-router-dom"
import "../CSS/CourseList.css"
import PlatformIntroModal from "../components/PlatformIntroModal"
import AuthFotoOnboarding from "../pages/AuthFotoOnboarding"

/* ============================
   Helpers para identificación única del usuario
   ============================ */
const decodeJwtPayload = (token) => {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const getUserKeyFromStorage = () => {
  try {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("accessToken");

    const payload = decodeJwtPayload(token);
    return (
      payload?.sub ||
      payload?.id ||
      payload?.user_id ||
      payload?.email ||
      payload?.codigo_institucional ||
      "anon"
    );
  } catch {
    return "anon";
  }
};

const safeKey = (s) => String(s || "").replace(/[^a-z0-9_-]/gi, "").slice(0, 48);

const getWelcomeTourCacheKey = (userNameOrId) =>
  `wt:welcomeTour:v1:${safeKey(userNameOrId)}`;

/* ============================
   CSS para el tutorial de bienvenida
   ============================ */
const CSS_WELCOME_TOUR = `
.welcome-tour-root{
  position: fixed;
  inset: 0;
  z-index: 99999;
  pointer-events: none;
}

.welcome-tour-dim{
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, .55);
  backdrop-filter: blur(7px) saturate(1.05);
  -webkit-backdrop-filter: blur(7px);
  animation: wtFade .18s ease-out both;
  pointer-events: auto;
}

@keyframes wtFade{
  from{ opacity: 0; }
  to{ opacity: 1; }
}

.welcome-tour-card{
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: min(560px, calc(100vw - 24px));
  background: #ffffff;
  border: 1px solid rgba(226,232,240,.98);
  border-radius: 18px;
  box-shadow: 0 34px 120px rgba(0,0,0,.45);
  z-index: 100002;
  padding: 14px 14px 12px;
  pointer-events: auto;
  animation: wtPop .22s ease-out both;
}

@keyframes wtPop{
  from{ transform: translate(-50%, -50%) translateY(8px) scale(.99); opacity: 0; }
  to{ transform: translate(-50%, -50%) translateY(0) scale(1); opacity: 1; }
}

.wt-head{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap: 12px;
  margin-bottom: 6px;
}

.wt-title{
  color:#0f172a;
  font-weight: 950;
  letter-spacing:.2px;
  font-size: 1.02rem;
}

.wt-step{
  font-weight: 900;
  font-size: .78rem;
  padding: 6px 10px;
  border-radius: 999px;
  background:#eef2ff;
  border:1px solid #c7d2fe;
  color:#3730a3;
  flex: 0 0 auto;
}

.wt-text{
  margin-top: 8px;
  color:#334155;
  line-height:1.55;
  font-size:.95rem;
}

.wt-p{ 
  margin: 0 0 8px 0; 
}

.wt-p:last-child{ 
  margin-bottom: 0; 
}

.wt-actions{
  margin-top: 12px;
  display:flex;
  align-items:center;
  justify-content:flex-end;
  gap: 10px;
}

.wt-btn{
  border:none;
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 950;
  cursor:pointer;
  transition: transform .12s ease, filter .12s ease, background .12s ease;
}

.wt-btn.primary{
  background: linear-gradient(135deg,#2563eb,#1d4ed8);
  color:#fff;
  border: 1px solid #1d4ed8;
  box-shadow: 0 14px 34px rgba(37,99,235,.22), inset 0 1px 0 rgba(255,255,255,.35);
}

.wt-btn.primary:hover{ 
  transform: translateY(-1px); 
  filter: brightness(1.05); 
}

.wt-btn.ghost{
  background:#fff;
  border:1px solid #e5e7eb;
  color:#0f172a;
}

.wt-btn.ghost:hover{ 
  background:#f8fafc; 
  transform: translateY(-1px); 
}
`;

/* ============================
   Helpers base / utilidades
   ============================ */
const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0))

const toNumber = (v, d = 0) => {
  const n = typeof v === "string" ? Number.parseFloat(v) : Number(v)
  return Number.isFinite(n) ? n : d
}

const getProgressColor = (pRaw) => {
  const p = toNumber(pRaw, 0)
  return p >= 80 ? "#24a148" : p >= 50 ? "#ff8a00" : p >= 20 ? "#f1c21b" : "#c6c6c6"
}

const computeEligibility = (course, getCourseProgress) => {
  const isExamOnly = String(course?.tipo || "").toLowerCase() === "examen_solo"
  const m = Number(course?.minExamPercentage)
  const min = Number.isFinite(m) ? Math.max(0, Math.min(100, m)) : 100
  const progress = clamp(getCourseProgress?.(course.id) ?? course.finalGrade ?? course.progress ?? 0)

  const completed =
    isExamOnly ||
    course?.enrollmentStatus === "completado" ||
    (course?.finalGrade != null ? Number(course.finalGrade) >= min : progress >= min)

  return { isExamOnly, min, progress, completed }
}

// ==== Helpers de fecha/hora del examen ====
const fmtTime = (t) => {
  if (!t) return ""
  try {
    return new Date(`2000-01-01T${String(t).slice(0, 5)}`).toLocaleTimeString("es-EC", {
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return String(t).slice(0, 5)
  }
}

const fmtDate = (d) => {
  if (!d) return ""
  try {
    return new Date(d).toLocaleDateString("es-EC", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  } catch {
    return String(d)
  }
}

function useExamBooking(courseId, userId, fetchMyExamBookings) {
  const [state, setState] = useState({ loading: true, book: null, error: "" })

  useEffect(() => {
    let alive = true
      ; (async () => {
        try {
          const mine = await fetchMyExamBookings?.({
            curso_id: Number(courseId),
            upcoming: true,
            user_id: userId,
          })
          const items = Array.isArray(mine?.bookings) ? mine.bookings : mine?.items || []
          const booking = items?.[0] || null

          if (alive) setState({ loading: false, book: booking, error: "" })
        } catch (e) {
          if (alive) setState({ loading: false, book: null, error: e?.message || "Error" })
        }
      })()

    return () => {
      alive = false
    }
  }, [courseId, userId, fetchMyExamBookings])

  return state
}

/* ============================================================
   Componente: Acciones rápidas de Examen (agenda / ir al examen)
   ============================================================ */
function ExamQuickAction({ course, examApi }) {
  const { fetchMyExamBookings, getCourseProgress } = examApi
  const { user } = useAuth()
  const navigate = useNavigate()

  const { loading: loadingBook, book, error: err } = useExamBooking(course?.id, user?.id, fetchMyExamBookings)

  const isExamOnly = String(course?.tipo || "").toLowerCase() === "examen_solo"
  const minRaw = Number(course?.minExamPercentage)
  const min = Number.isFinite(minRaw) ? minRaw : 100
  const localProgress = Number(getCourseProgress?.(course.id) || 0)

  const completed =
    isExamOnly ||
    course?.enrollmentStatus === "completado" ||
    (course?.finalGrade != null ? Number(course.finalGrade) >= min : localProgress >= min)

  const loadingAll = loadingBook

  if (loadingAll) {
    return (
      <div className="exam-card loading">
        <div className="exam-card-header">
          <div className="exam-card-header-left">
            <span className="exam-emoji" role="img" aria-label="exam">
              📝
            </span>
            <span className="exam-title">Examen</span>
          </div>
          <div className="exam-skel-bar" />
        </div>
      </div>
    )
  }

  if (!book && !completed) return null

  return (
    <div className="exam-card">
      <div className="exam-card-header">
        <div className="exam-card-header-left">
          <span className="exam-emoji" role="img" aria-label="exam">
            📝
          </span>
          <span className="exam-title">Examen</span>

          {String(course?.tipo || "").toLowerCase() === "examen_solo" && (
            <span className="exam-badge">Examen único</span>
          )}
        </div>

        {err ? (
          <span className="exam-error">Error: {err}</span>
        ) : book ? (
          <div className="exam-card-right">
            <span className="pill-ok">Agendado</span>
            <button
              className="enroll-btn student-btn btn-sm"
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/curso/${course.code}/examen`)
              }}
            >
              Ir al examen
            </button>
          </div>
        ) : (
          <button
            className="enroll-btn student-btn btn-sm"
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/curso/${course.code}/examen`)
            }}
            title="Agendar examen"
          >
            Agendar examen
          </button>
        )}
      </div>

      {book && (
        <div className="exam-extra">
          <span>
            📅 {fmtDate(book.fecha_examen)} · ⏰ {fmtTime(book.hora_inicio)}–{fmtTime(book.hora_fin)}
          </span>
          {book?.is_virtual != null && <span className="exam-mode"> · {book.is_virtual ? "Virtual" : "Presencial"}</span>}
        </div>
      )}
    </div>
  )
}

function ExamInlineButton({ course, examApi }) {
  const { fetchMyExamBookings } = examApi
  const { user } = useAuth()
  const navigate = useNavigate()

  const { loading: loadingBook, book } = useExamBooking(course?.id, user?.id, fetchMyExamBookings)
  const label = loadingBook ? "Examen" : book ? "Ir examen" : "Agendar Examen"

  return (
    <button
      className="enroll-btn student-btn exam-split-btn"
      onClick={(e) => {
        e.stopPropagation()
        navigate(`/curso/${course.code}/examen`)
      }}
      type="button"
      title={book ? "Tienes un examen agendado" : "Agendar examen"}
    >
      {label}
    </button>
  )
}

/* ============================================================
   TUTORIAL DE BIENVENIDA (2 PASOS)
   ============================================================ */
function WelcomeTourModal({ open, step, onNext, onSkip, storageKey }) {

  if (!open) return null;

  const STEPS = [
    {
      title: "¡BIENVENIDO A LA PLATAFORMA DE AUTOAPRENDIZAJE!",
      text: "Esta plataforma está diseñada para potenciar tu aprendizaje de forma autónoma y progresiva.\n\nAquí podrás acceder a tus cursos, seguir tu progreso y mucho más."
    },
    {
      title: "TUS CURSOS DISPONIBLES",
      text: "Aquí puedes ver todos los cursos en los que estás inscrito.\n\nPara ingresar a tus cursos lo podrás hacer desde el botón 'Ingresar'.\n\nCada curso tiene su propia estructura de niveles, módulos y componentes que deberás completar."
    }
  ];

  const currentStep = STEPS[step] || STEPS[0];
  const isLast = step === STEPS.length - 1;

  const node = (
    <>
      <style>{CSS_WELCOME_TOUR}</style>

      <div className="welcome-tour-root" role="dialog" aria-modal="true">
        <div
          className="welcome-tour-dim"
          onClick={isLast ? onNext : undefined}
        />

        <div
          className="welcome-tour-card"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="wt-head">
            <strong className="wt-title">{currentStep.title}</strong>
            <span className="wt-step">{step + 1}/{STEPS.length}</span>
          </div>

          <div className="wt-text">
            {currentStep.text.split('\n\n').map((p, i) => (
              <p key={i} className="wt-p">{p}</p>
            ))}
          </div>

          <div className="wt-actions">
            {step === 0 && (
              <button
                className="wt-btn ghost"
                onClick={onSkip}
                type="button"
              >
                Omitir
              </button>
            )}

            <button
              className="wt-btn primary"
              onClick={onNext}
              type="button"
            >
              {isLast ? 'Comenzar' : 'Siguiente'}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(node, document.body);
}

/* ============================================================
   FILA DE LISTA (vista "list") PARA ESTUDIANTE
   ============================================================ */
function StudentListRow({
  course,
  realProgress,
  onEnterCourse,
  studentStatus,
  showExamBtn,
  isExamOpen,
  toggleExam,
  examApi,
}) {
  return (
    <div
      className="course-row compact-row student"
      role="button"
      tabIndex={0}
      onClick={() => onEnterCourse()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onEnterCourse()
        }
      }}
    >
      <div className="compact-col--name">{course.title}</div>
      <div className="compact-col--code">{course.code || "—"}</div>
      <div className="compact-col--desc">{course.description || "—"}</div>

      <div className="compact-col--date">
        {course.enrollmentDate ? course.enrollmentDate.toLocaleDateString("es-EC") : "—"}
      </div>

      <div className="compact-col--progress">
        <div className="compact-progress">
          <span className="progress-number">
            {realProgress}% - Min: {course.minExamPercentage ?? 100}% 🎯
          </span>
          <div className="progress-bar xs">
            <div className="progress-fill" style={{ width: `${realProgress}%` }} />
          </div>
        </div>
      </div>

      <div className="compact-col--status">
        {studentStatus ? <span className={`status-badge xs ${studentStatus.color}`}>{studentStatus.text}</span> : "—"}
      </div>

      <div className="compact-col--actions">
        <button
          className="row-enter-btn student-btn btn-sm"
          onClick={(e) => {
            e.stopPropagation()
            onEnterCourse()
          }}
        >
          Ingresar
        </button>

        {showExamBtn && (
          <div className="exam-holder">
            <button
              className="row-enter-btn outline btn-sm"
              onClick={(e) => {
                e.stopPropagation()
                toggleExam(course.id)
              }}
              title="Examen"
            >
              Examen
            </button>

            {isExamOpen && (
              <div className="exam-pop" onClick={(e) => e.stopPropagation()}>
                <ExamQuickAction course={course} examApi={examApi} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/* ============================================================
   FILA DE LISTA (vista "list") PARA DOCENTE / ASISTENTE
   ============================================================ */
function TeacherListRow({ course, navigate }) {
  const role = String(course.assignmentRole || "docente").toLowerCase()
  const roleLabel = role === "asistente" ? "Asistente" : "Docente"

  const statusIsActive = course.active && course.isActiveAssignment !== false

  return (
    <div
      className="course-row compact-row teacher"
      role="button"
      tabIndex={0}
      onClick={() => {
        if (!course.code) return
        navigate(`/curso/${course.code}`)
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          if (!course.code) return
          navigate(`/curso/${course.code}`)
        }
      }}
    >
      <div className="compact-col--name">{course.title}</div>
      <div className="compact-col--code">{course.code || "—"}</div>
      <div className="compact-col--desc">{course.description || "—"}</div>

      <div className="compact-col--date">{roleLabel}</div>

      <div className="compact-col--progress">👥 {course.myStudents ?? 0}</div>

      <div className="compact-col--status">
        <span className={`status-badge xs ${statusIsActive ? "green" : "red"}`}>
          {course.active
            ? course.isActiveAssignment === false
              ? "Asignación inactiva"
              : "Activo"
            : "Curso inactivo"}
        </span>
      </div>

      <div className="compact-col--actions">
        <button
          className="row-enter-btn teacher-btn btn-sm"
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            if (!course.code) return
            navigate(`/curso/${course.code}`)
          }}
          disabled={!statusIsActive}
        >
          {statusIsActive ? "Entrar" : "No disponible"}
        </button>

        <button
          className="row-enter-btn teacher-btn outline btn-sm"
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            if (!course.code) return
            navigate(`/admin/cursos/${course.id}`, { state: { course } })
          }}
          disabled={!statusIsActive}
          title="Configurar curso"
        >
          Configurar
        </button>
      </div>
    </div>
  )
}

/* ============================================================
   FILA DE LISTA (vista "list") PARA ADMIN / DOCENTE
   ============================================================ */
function AdminListRow({ course, navigate }) {
  return (
    <div className="course-row compact-row admin">
      <div className="compact-col--name">{course.title}</div>
      <div className="compact-col--code">{course.code || "—"}</div>
      <div className="compact-col--desc">{course.description || "—"}</div>

      <div className="compact-col--progress">🎯 {course.minExamPercentage ?? 100}%</div>
      <div className="compact-col--progress">👥 {course.totalStudents ?? 0}</div>
      <div className="compact-col--progress">👨‍🏫 {course.totalTeachers ?? 0}</div>

      <div className="compact-col--status">
        <span className={`status-badge xs ${course.active ? "green" : "red"}`}>{course.active ? "Activo" : "Inactivo"}</span>
      </div>

      <div className="compact-col--actions">
        <button
          className="row-enter-btn admin-btn btn-sm"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/curso/${course.code}`)
          }}
        >
          Visualizar
        </button>

        <button
          className="row-enter-btn admin-btn outline btn-sm"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/admin/cursos/${course.id}`, { state: { course } })
          }}
        >
          Configurar
        </button>
      </div>
    </div>
  )
}

/* ============================================================
   TARJETA DE CURSO (vista "grid" / cuadrícula)
   ============================================================ */
function CourseCardGrid({ course, navigate, getCourseProgress, examApi, onEnterCourse, studentStatus, view }) {
  const { isAdmin, isTeacher, isStudent, roleLower } = view

  const realProgress = clamp(Number(getCourseProgress?.(course.id) ?? course.finalGrade ?? course.progress ?? 0))
  const tipoLower = String(course?.tipo || "").toLowerCase()
  const hasExam = tipoLower.includes("examen")

  const chips = (() => {
    if (isAdmin) {
      return [
        { k: "🎯 Mínimo del curso", v: `${course.minExamPercentage ?? 100}%` },
        { k: "👥 Estudiantes", v: `${course.totalStudents ?? 0}` },
        { k: "👨‍🏫 Docentes", v: `${course.totalTeachers ?? 0}` },
      ]
    }

    if (isTeacher) {
      const role = String(course.assignmentRole || roleLower || "docente").toLowerCase()
      const label = role === "asistente" ? "Asistente" : "Docente"
      return [
        { k: "🎓 Rol en este curso", v: label },
        { k: "🎯 Mínimo del curso", v: `${course.minExamPercentage ?? 100}%` },
        { k: "👥 Mis estudiantes", v: `${course.myStudents ?? 0}` },
      ]
    }

    const base = [
      { k: "📅 Inscripción", v: course.enrollmentDate ? course.enrollmentDate.toLocaleDateString("es-EC") : "—" },
      { k: "🎯 Mínimo del curso", v: `${course.minExamPercentage ?? 100}%` },
    ]
    if (course.finalGrade != null) base.push({ k: "✅ Nota", v: `${Number(course.finalGrade).toFixed(0)}%` })
    return base
  })()

  const onCardClick = () => {
    if (isStudent) onEnterCourse?.()
  }

  return (
    <article
      className={
        "course-card pro-card " +
        (course.featured ? "featured " : "") +
        (!course.active ? "inactive " : "") +
        (isStudent ? "clickable " : "")
      }
      role={isStudent ? "button" : undefined}
      tabIndex={isStudent ? 0 : undefined}
      onClick={isStudent ? onCardClick : undefined}
      onKeyDown={
        isStudent
          ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onCardClick()
            }
          }
          : undefined
      }
    >
      {course.featured && <div className="featured-badge">Destacado</div>}
      {!course.active && <div className="inactive-badge">Inactivo</div>}
      {hasExam && <div className="corner-pill">EXAMEN</div>}

      <div className="pro-media">
        <div className="course-image">
          <img src={course.image || "/placeholder.svg"} alt={course.title} />
          <div className="course-overlay" />
        </div>
      </div>

      <div className="pro-body">
        <div className="pro-title-row">
          <h3 className="course-title" title={course.title}>
            {course.title}
          </h3>
          {course.code && <span className="course-code">{course.code}</span>}
        </div>

        <p className="course-description">
          {course.description?.trim() ? course.description : "Sin descripción disponible."}
        </p>

        {isStudent && studentStatus && (
          <div className="pro-status-line">
            <span className={`status-badge xs ${studentStatus.color}`}>{studentStatus.text}</span>
            {course.finalGrade != null && (
              <span
                className={`grade-pill ${Number(course.finalGrade) >= (course.minExamPercentage ?? 100) ? "ok" : "bad"
                  }`}
              >
                {Number(course.finalGrade).toFixed(0)}%
              </span>
            )}
          </div>
        )}

        <div className="pro-chips">
          {chips.map((c) => (
            <div key={c.k} className="pro-chip" title={`${c.k}: ${c.v}`}>
              <span className="pro-chip-k">{c.k}</span>
              <span className="pro-chip-v">{c.v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="pro-footer">
        {isStudent && (
          <>
            <div className="inline-progress">
              <div className="inline-progress-top">
                <span>
                  Progreso: <strong>{realProgress}%</strong>
                </span>
              </div>
              <div className="progress-bar inline">
                <span className="progress-fill" style={{ width: `${realProgress}%`, background: getProgressColor(realProgress) }} />
              </div>
            </div>

            <div className={`grid-actions ${hasExam ? "split" : ""}`}>
              {hasExam && <ExamInlineButton course={course} examApi={examApi} />}
              <button
                className="enroll-btn student-btn access-split-btn"
                data-tour="home-continue"
                onClick={(e) => {
                  e.stopPropagation()
                  onEnterCourse()
                }}
                disabled={course.enrollmentStatus === "completado" || course.enrollmentStatus === "retirado"}
                type="button"
              >
                {course.enrollmentStatus === "activo"
                  ? "Continuar"
                  : course.enrollmentStatus === "completado"
                    ? "Completado"
                    : course.enrollmentStatus === "retirado"
                      ? "Retirado"
                      : "Acceder"}
              </button>
            </div>
          </>
        )}

        {isAdmin && (
          <div className="pro-actions-row">
            <span className={`status-indicator ${course.active ? "active" : "inactive"}`}>{course.active ? "ACTIVO" : "INACTIVO"}</span>

            <div className="pro-actions">
              <button
                className="enroll-btn admin-btn outline"
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/admin/cursos/${course.id}`, { state: { course } })
                }}
              >
                Configurar
              </button>

              <button
                className="enroll-btn admin-btn"
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/curso/${course.code}`)
                }}
              >
                Visualizar
              </button>
            </div>
          </div>
        )}

        {isTeacher && (
          <div className="pro-actions-row">
            <span className={`status-indicator ${course.active && course.isActiveAssignment !== false ? "active" : "inactive"}`}>
              {course.active ? (course.isActiveAssignment === false ? "Asignación inactiva" : "Activo") : "Curso inactivo"}
            </span>

            <div className="pro-actions">
              <button
                className="enroll-btn teacher-btn outline"
                disabled={!course.active || course.isActiveAssignment === false}
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  if (!course.code) return
                  navigate(`/admin/cursos/${course.id}`, { state: { course } })
                }}
              >
                Configurar
              </button>

              <button
                className="enroll-btn teacher-btn"
                disabled={!course.active || course.isActiveAssignment === false}
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  if (!course.code) return
                  navigate(`/curso/${course.code}`)
                }}
              >
                {(() => {
                  const role = String(course.assignmentRole || roleLower || "docente").toLowerCase()
                  if (!course.active || course.isActiveAssignment === false) return "No disponible"
                  return role === "asistente" ? "Asistir" : "Enseñar"
                })()}
              </button>
            </div>
          </div>
        )}
      </div>
    </article>
  )
}

/* ============================================================
   COMPONENTE PRINCIPAL CourseList
   ============================================================ */
export default function CourseList() {
  const {
    fetchCourses,
    fetchStudentCourses,
    fetchTeacherCourses,
    fetchMyExamBookings,
    getCourseProgress,
    prefetchCoursesProgress,
    acceptPlatformTerms,
    checkPlatformTermsAcceptance,
    fetchRegistrationTerms, // ✅ NUEVO: obtiene T&C de registro desde BD
  } = useFuncion()

  const { currentRole, user, refreshUser, isCameraRegistrationActive } = useAuth()
  const navigate = useNavigate()

  const roleLower = (currentRole || "").toLowerCase()
  const isAdmin = roleLower === "administrador" || roleLower === "admin"
  const isTeacher = roleLower === "docente" || roleLower === "asistente"
  const isStudent = roleLower === "estudiante" || (!isAdmin && !isTeacher)

  const fetchingRef = useRef(false)
  const lastFetchKey = useRef("")

  const [allCourses, setAllCourses] = useState([])
  const [initialLoading, setInitialLoading] = useState(true)
  const [error, setError] = useState(null)

  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")
  const [typeFilter, setTypeFilter] = useState("todos")
  const [currentPage, setCurrentPage] = useState(1)
  const coursesPerPage = 20

  const [layout, setLayout] = useState(() => {
    try {
      const saved = localStorage.getItem("courseListLayout")
      if (saved === "grid" || saved === "list") return saved
      if (saved === "one" || saved === "two") return "grid"
      return "grid"
    } catch {
      return "grid"
    }
  })
  const changeLayout = (mode) => {
    setLayout(mode)
    try {
      localStorage.setItem("courseListLayout", mode)
    } catch { }
  }

  const [openExamId, setOpenExamId] = useState(null)

  // Modal de bienvenida + TyC
  const [introOpen, setIntroOpen] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [needsPhoto, setNeedsPhoto] = useState(false);

  // ===== Tutorial de bienvenida =====
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);
  const [welcomeStep, setWelcomeStep] = useState(0);

  // ✅ userKey DEBE estar AQUÍ (una sola vez)
  const userKey = useMemo(
    () => user?.id || user?.email || getUserKeyFromStorage(),
    [user?.id, user?.email]
  );
  const welcomeTourKey = useMemo(
    () => getWelcomeTourCacheKey(userKey),
    [userKey]
  );

  // ✅ NUEVO: términos desde BD
  const [regTermsLoading, setRegTermsLoading] = useState(false)
  const [regTermsError, setRegTermsError] = useState("")
  const [regTermsData, setRegTermsData] = useState(null)

  const introKey = useMemo(() => {
    const uid = user?.id || user?.email || "anon"
    return `intro:platform:v1:${uid}`
  }, [user?.id, user?.email])

  const toggleExam = (id) => setOpenExamId((prev) => (prev === id ? null : id))

  const hasAuthFoto = useMemo(() => {
    return Boolean(user?.auth_foto?.public_url || user?.auth_foto?.url || user?.auth_foto_url)
  }, [user])

  // ✅ NUEVO: cargar términos de registro desde BD cuando el modal se abre
  useEffect(() => {
    if (!introOpen) return
    if (!fetchRegistrationTerms) return

    let alive = true
      ; (async () => {
        try {
          setRegTermsLoading(true)
          setRegTermsError("")
          const res = await fetchRegistrationTerms()
          if (!alive) return

          if (res?.success) {
            setRegTermsData(res?.terms || null)
          } else {
            setRegTermsData(null)
            setRegTermsError(res?.message || "No se pudo cargar los términos.")
          }
        } catch (e) {
          if (!alive) return
          setRegTermsData(null)
          setRegTermsError(e?.message || "Error al cargar los términos.")
        } finally {
          if (!alive) return
          setRegTermsLoading(false)
        }
      })()

    return () => {
      alive = false
    }
  }, [introOpen, fetchRegistrationTerms])

  // Reemplaza el useEffect de checkTerms existente con este:
  useEffect(() => {
    if (!user?.id) return

    const checkTerms = async () => {
      try {
        const seenLocally = localStorage.getItem(introKey) === "1"
        const isCameraActive = isCameraRegistrationActive ? isCameraRegistrationActive() : true

        if (seenLocally) {
          setIntroOpen(false)
          const photoNeeded = isCameraActive && !hasAuthFoto
          setNeedsPhoto(photoNeeded)

          // ✅ Si no necesita foto, verificar tour aquí directamente
          if (!photoNeeded) {
            try {
              const tourSeen = localStorage.getItem(welcomeTourKey) === "1"
              if (!tourSeen) {
                setWelcomeStep(0)
                setShowWelcomeTour(true)
              }
            } catch { }
          }
          return
        }

        const result = await checkPlatformTermsAcceptance()

        if (result.success && result.aceptado) {
          localStorage.setItem(introKey, "1")
          setIntroOpen(false)
          const photoNeeded = isCameraActive && !hasAuthFoto
          setNeedsPhoto(photoNeeded)

          // ✅ Si no necesita foto, verificar tour aquí directamente
          if (!photoNeeded) {
            try {
              const tourSeen = localStorage.getItem(welcomeTourKey) === "1"
              if (!tourSeen) {
                setWelcomeStep(0)
                setShowWelcomeTour(true)
              }
            } catch { }
          }
        } else {
          setIntroOpen(true)
          setTermsAccepted(false)
        }
      } catch (error) {
        console.error("❌ Error al verificar términos:", error)
        const seenLocally = localStorage.getItem(introKey) === "1"
        if (seenLocally) {
          setIntroOpen(false)
          const isCameraActive = isCameraRegistrationActive ? isCameraRegistrationActive() : true
          const photoNeeded = isCameraActive && !hasAuthFoto
          setNeedsPhoto(photoNeeded)
          if (!photoNeeded) {
            try {
              const tourSeen = localStorage.getItem(welcomeTourKey) === "1"
              if (!tourSeen) {
                setWelcomeStep(0)
                setShowWelcomeTour(true)
              }
            } catch { }
          }
        } else {
          setIntroOpen(true)
        }
      }
    }

    checkTerms()
  }, [user?.id, introKey, checkPlatformTermsAcceptance, hasAuthFoto, welcomeTourKey, isCameraRegistrationActive])

  /* ======================
     Cargar cursos según rol
     ====================== */
  const getCourses = useCallback(
    async (page = 1) => {
      const fetchKey = `${currentRole}-${page}`
      if (fetchingRef.current && lastFetchKey.current === fetchKey) return

      fetchingRef.current = true
      lastFetchKey.current = fetchKey

      try {
        setError(null)

        // ✅ NUEVO: Intentar leer caché primero
        const cacheKey = cacheManager.getCourseListCacheKey(
          currentRole || 'estudiante',
          userKey
        );

        const cached = cacheManager.readCache(cacheKey, {
          storageType: 'session',
          currentVersion: cacheManager.CURRENT_VERSIONS.COURSE_LIST,
        });

        // ✅ Si hay caché fresco, usarlo inmediatamente (SWR)
        if (cached?.courses) {
          console.log('💾 Using cached course list');
          const rehydrated = cached.courses.map(c => ({
            ...c,
            enrollmentDate: c.enrollmentDate ? new Date(c.enrollmentDate) : null,
            statusDate: c.statusDate ? new Date(c.statusDate) : null,
          }));
          setAllCourses(rehydrated);
          setInitialLoading(false);

          // ✅ Si está muy fresco (< 2 min), no revalidar
          const age = Date.now() - cached.ts;
          if (age < 120000) { // 2 minutos
            fetchingRef.current = false;
            return;
          }
          // Si tiene 2-5 min, revalidar en background
        }

        // ✅ Fetch del backend (normal o revalidación)
        let data = []

        if (isAdmin) {
          const res = await fetchCourses({ page, limit: coursesPerPage, showAll: false })
          console.log('🔍 [Admin] fetchCourses res:', res)
          data = Array.isArray(res) ? res : res?.courses || res?.data || []
        } else if (isTeacher) {
          data = await fetchTeacherCourses({ page, limit: coursesPerPage })
          console.log('🔍 [Teacher] fetchTeacherCourses data:', data)
        } else if (isStudent) {
          data = await fetchStudentCourses({ page, limit: coursesPerPage })
          console.log('🔍 [Student] fetchStudentCourses data:', data)
        }
        console.log('🔍 data final:', data, 'isArray:', Array.isArray(data))

        const transformedCourses = data.map((course) => {
          const courseData = course.curso || course
          const baseData = {
            id: courseData.id,
            title: courseData.nombre,
            description: courseData.descripcion,
            code: courseData.codigo_curso,
            minExamPercentage: Number.parseFloat(
              courseData.porcentaje_minimo_examen ?? courseData.porcentaje_minimo_aprobacion ?? 100,
            ),
            active: courseData.activo,
            createdById: courseData.creado_por_id,
            createdByName: courseData.creado_por_nombre,
            createdAt: courseData.fecha_creacion,
            updatedAt: courseData.fecha_actualizacion,
            category: "Curso",
            image: "https://i.postimg.cc/FKxNvvRd/cursos-online-gratuitos-seguridad-inform-tica.jpg",
            featured: courseData.id % 5 === 0,
            tipo: courseData.tipo,
            totalStudents: Number(courseData.total_estudiantes ?? 0),
            totalTeachers: Number(courseData.total_docentes ?? 0),
          }

          if (isAdmin) return baseData

          if (isTeacher) {
            const assignmentRoleRaw =
              course.rol ||
              course.role ||
              course.tipo_asignacion ||
              course.assignment_role ||
              course.assignmentType ||
              "docente"

            const normalizedAssignmentRole = String(assignmentRoleRaw).toLowerCase().includes("asistente")
              ? "asistente"
              : "docente"

            return {
              ...baseData,
              assignmentRole: normalizedAssignmentRole,
              assignmentDate: course.fecha_asignacion || course.assignment_date || null,
              isActiveAssignment:
                course.asignacion_activa !== undefined && course.asignacion_activa !== null
                  ? !!course.asignacion_activa
                  : course.activo !== undefined && course.activo !== null
                    ? !!course.activo
                    : baseData.active,
              averageGrade: course.averageGrade ?? null,
              myStudents: Number(course.myStudents ?? course.total_estudiantes ?? baseData.totalStudents ?? 0),
            }
          }

          if (isStudent) {
            return {
              ...baseData,
              enrollmentStatus: course.estado || "inscrito",
              finalGrade: course.nota_final ? Number.parseFloat(course.nota_final) : null,
              enrollmentDate: course.fecha_inscripcion ? new Date(course.fecha_inscripcion) : null,
              statusDate: course.fecha_estado ? new Date(course.fecha_estado) : null,
              progress: course.nota_final
                ? Math.min(100, Number.parseFloat(course.nota_final))
                : Number.isFinite(Number(course.progreso))
                  ? Number(course.progreso)
                  : 0,
            }
          }

          return baseData
        })

        setAllCourses(transformedCourses)

        // ✅ NUEVO: Guardar en caché
        cacheManager.writeCache(
          cacheKey,
          { courses: transformedCourses },
          {
            storageType: 'session',
            ttl: cacheManager.DEFAULT_TTL.COURSE_LIST,
            cacheVersion: cacheManager.CURRENT_VERSIONS.COURSE_LIST,
          }
        );

      } catch (err) {
        setError(err.message)
      } finally {
        setInitialLoading(false)
        fetchingRef.current = false
      }
    },
    [
      isAdmin,
      isTeacher,
      isStudent,
      coursesPerPage,
      currentRole,
      userKey,
      fetchCourses,
      fetchTeacherCourses,
      fetchStudentCourses,
    ],
  )

  useEffect(() => {
    cacheManager.clearExpiredCache();
  }, []);

  useEffect(() => {
    getCourses(currentPage)
  }, [currentRole, currentPage, getCourses])

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300)
    return () => clearTimeout(t)
  }, [searchTerm])

  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) setCurrentPage(1)
  }, [debouncedSearchTerm, searchTerm])

  const filteredCourses = allCourses.filter((course) => {
    if (isTeacher && !isAdmin) {
      const assigned = String(course.assignmentRole || "").toLowerCase()
      if (roleLower === "docente" && assigned !== "docente") return false
      if (roleLower === "asistente" && assigned !== "asistente") return false
    }

    const tipoLower = String(course?.tipo || "").toLowerCase()
    const isExamOnly = tipoLower === "examen_solo"
    const isRegular = !isExamOnly

    if (typeFilter === "examen" && !isExamOnly) return false
    if (typeFilter === "regular" && !isRegular) return false

    if (!debouncedSearchTerm.trim()) return true
    const s = debouncedSearchTerm.toLowerCase().trim()

    return (
      String(course.title || "").toLowerCase().includes(s) ||
      String(course.code || "").toLowerCase().includes(s) ||
      (course.description && String(course.description).toLowerCase().includes(s))
    )
  })

  const startIndex = (currentPage - 1) * coursesPerPage
  const endIndex = startIndex + coursesPerPage
  const paginatedCourses = filteredCourses.slice(startIndex, endIndex)
  const totalPages = Math.ceil(filteredCourses.length / coursesPerPage)

  useEffect(() => {
    const ids = paginatedCourses.map((c) => c.id)
    prefetchCoursesProgress?.(ids, { concurrency: 3 }).catch(() => { })

    const nextStart = currentPage * coursesPerPage
    const nextIds = filteredCourses.slice(nextStart, nextStart + coursesPerPage).map((c) => c.id)
    if (nextIds.length) prefetchCoursesProgress?.(nextIds, { concurrency: 2 }).catch(() => { })
  }, [paginatedCourses, filteredCourses, currentPage, coursesPerPage, prefetchCoursesProgress])

  useEffect(() => {
    setCurrentPage(1)
  }, [typeFilter])

  const handleRetry = () => getCourses(currentPage)

  const getNoResultsMessage = () => {
    if (currentRole === "estudiante")
      return "No tienes cursos asignados en este momento. Contacta al administrador para más información."
    if (isTeacher) return "No tienes cursos asignados como docente/asistente."
    if (currentRole === "administrador") return "No hay cursos disponibles en este momento."
    return "No se encontraron cursos."
  }

  const enterCourseAndStoreFlags = (course) => {
    const flags = computeEligibility(course, getCourseProgress)
    try {
      sessionStorage.setItem(
        `courseFlags:${course.code}`,
        JSON.stringify({
          examEligible: !!flags.completed,
          completedCourse: !!flags.completed,
          minRequired: flags.min,
          progress: flags.progress,
        }),
      )
    } catch { }
    navigate(`/curso/${course.code}`, {
      state: {
        examEligible: !!flags.completed,
        completedCourse: !!flags.completed,
        minRequired: flags.min,
        progress: flags.progress,
      },
    })
  }

  const onPhotoSaved = useCallback(async () => {
    try {
      setNeedsPhoto(false);
      await refreshUser();

      // ✅ Verificar si ya vio el tutorial (por ID de estudiante en sessionStorage)
      try {
        const tourSeen = localStorage.getItem(welcomeTourKey) === "1";
        if (!tourSeen) {
          setWelcomeStep(0);
          setShowWelcomeTour(true);
        }
      } catch { }

    } catch {
      setNeedsPhoto(false);
    }
  }, [refreshUser, welcomeTourKey]);

  const acceptAndContinue = useCallback(async () => {
    if (!user?.id) {
      alert("No se pudo identificar al usuario")
      return
    }

    try {
      const result = await acceptPlatformTerms({
        ip: null,
        ua: navigator?.userAgent || null,
      })

      // guarda local
      localStorage.setItem(introKey, "1")
      setIntroOpen(false)
      const isCameraActive = isCameraRegistrationActive ? isCameraRegistrationActive() : true
      setNeedsPhoto(isCameraActive && !hasAuthFoto)

      return result
    } catch (error) {
      console.error("❌ Error completo:", error)

      if (error.message?.includes("401") || error.message?.includes("UNAUTHORIZED")) {
        alert("Tu sesión expiró. Por favor, inicia sesión nuevamente.")
        localStorage.clear()
        window.location.href = "/login"
        return
      }

      alert(`Hubo un error: ${error.message || "Error desconocido"}`)
    }
  }, [user?.id, introKey, acceptPlatformTerms, hasAuthFoto])

  if (initialLoading && allCourses.length === 0) {
    return <LoadingSpinner message="Cargando cursos..." />
  }

  if (error) {
    return (
      <div className="courses-container">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Error al cargar los cursos</h3>
          <p>{error}</p>
          <button onClick={handleRetry} className="retry-btn">
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="courses-page">
      {needsPhoto && <AuthFotoOnboarding embedded={true} onPhotoSaved={onPhotoSaved} />}

      {!needsPhoto && (
        <div className="courses-container">
          <div className="header-combo">
            <div className="hc-left">
              <h1 className="page-title">{isAdmin ? "Gestión de Cursos" : "Mis Cursos"}</h1>
              <p className="page-subtitle">
                {isAdmin
                  ? "Administra todos los cursos de la plataforma"
                  : isTeacher
                    ? "Gestiona tus cursos asignados"
                    : "Accede a tus cursos y revisa tu progreso"}
              </p>
            </div>

            <div className="mobile-title">
              <h1 className="page-title">{isAdmin ? "Gestión de Cursos" : "Mis Cursos"}</h1>
              <p className="page-subtitle">
                {isAdmin
                  ? "Administra todos los cursos de la plataforma"
                  : isTeacher
                    ? "Gestiona tus cursos asignados"
                    : "Accede a tus cursos y revisa tu progreso"}
              </p>
            </div>

            <div className="hc-right">
              <div className="hc-filters">
                <div className="search-wrapper" data-tour="home-search">
                  <span className="search-icon">🔍</span>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Buscar por nombre o código..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                      if (e.target.value.trim()) setCurrentPage(1)
                    }}
                  />
                </div>

                <div className="type-filter" data-tour="home-typefilter">
                  <select
                    value={typeFilter}
                    onChange={(e) => {
                      setTypeFilter(e.target.value)
                      setCurrentPage(1)
                    }}
                    className="type-filter-select"
                    title="Filtrar por tipo"
                  >
                    <option value="todos">Todos</option>
                    <option value="regular">Cursos normales</option>
                    <option value="examen">Solo examen</option>
                  </select>
                </div>
              </div>

              <div className="view-toggle">
                <span className="vt-label">Vista:</span>
                <button
                  className="vt-btn"
                  aria-pressed={layout === "list"}
                  onClick={() => changeLayout("list")}
                  title="Vista de lista (filas)"
                >
                  Lista
                </button>
                <button
                  className="vt-btn"
                  aria-pressed={layout === "grid"}
                  onClick={() => changeLayout("grid")}
                  title="Vista de tarjetas"
                >
                  Cuadrícula
                </button>
              </div>
            </div>
          </div>

          {(isAdmin || isTeacher) && allCourses.length > 0 && (
            <div className="stats-bar">
              {isAdmin && (
                <>
                  <div className="stat-item">
                    <span className="stat-number">{filteredCourses.length}</span>
                    <span className="stat-label">Cursos {searchTerm ? "encontrados" : ""}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">
                      {allCourses.reduce((sum, c) => sum + (c.totalStudents || 0), 0).toLocaleString()}
                    </span>
                    <span className="stat-label">Estudiantes</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{allCourses.reduce((sum, c) => sum + (c.totalTeachers || 0), 0)}</span>
                    <span className="stat-label">Docentes</span>
                  </div>
                </>
              )}

              {isTeacher && (
                <>
                  <div className="stat-item">
                    <span className="stat-number">{filteredCourses.length}</span>
                    <span className="stat-label">Mis Cursos {searchTerm ? "encontrados" : ""}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">
                      {allCourses.reduce((sum, c) => sum + (c.myStudents || 0), 0)}
                    </span>
                    <span className="stat-label">Estudiantes</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{allCourses.filter((c) => c.averageGrade >= 80).length}</span>
                    <span className="stat-label">Exitosos</span>
                  </div>
                </>
              )}
            </div>
          )}

          <div className={`courses-list ${layout === "list" ? "list-rows" : "grid-cards"}`} data-tour="home-courses">
            {layout === "list" && paginatedCourses.length > 0 && (
              <div className={"list-header-row " + (isStudent ? "student" : isAdmin ? "admin" : "generic")}>
                {isStudent && (
                  <>
                    <div className="lh-col lh-name">Nombre</div>
                    <div className="lh-col lh-code">Código</div>
                    <div className="lh-col lh-desc">Descripción</div>
                    <div className="lh-col lh-date">Fecha de inscripción</div>
                    <div className="lh-col lh-progress">% / Mínimo examen</div>
                    <div className="lh-col lh-status">Estado</div>
                    <div className="lh-col lh-actions">Acciones</div>
                  </>
                )}

                {isAdmin && (
                  <>
                    <div className="lh-col lh-name">Nombre</div>
                    <div className="lh-col lh-code">Código</div>
                    <div className="lh-col lh-desc">Descripción</div>
                    <div className="lh-col lh-progress">Mín. aprobación</div>
                    <div className="lh-col lh-students">Estudiantes</div>
                    <div className="lh-col lh-teachers">Docentes</div>
                    <div className="lh-col lh-status">Estado</div>
                    <div className="lh-col lh-actions">Acciones</div>
                  </>
                )}

                {isTeacher && !isAdmin && (
                  <>
                    <div className="lh-col lh-name">Nombre</div>
                    <div className="lh-col lh-code">Código</div>
                    <div className="lh-col lh-desc">Descripción</div>
                    <div className="lh-col lh-date">Rol en curso</div>
                    <div className="lh-col lh-students">Mis estudiantes</div>
                    <div className="lh-col lh-status">Estado</div>
                    <div className="lh-col lh-actions">Acciones</div>
                  </>
                )}
              </div>
            )}

            {paginatedCourses.length === 0 ? (
              <div className="no-results">
                <div className="no-results-icon">📚</div>
                <h3>{searchTerm ? "No se encontraron cursos" : "No hay cursos disponibles"}</h3>
                <p>{searchTerm ? `No se encontraron cursos que coincidan con "${searchTerm}"` : getNoResultsMessage()}</p>

                {searchTerm && (
                  <button
                    className="no-results-action"
                    onClick={() => {
                      setSearchTerm("")
                      setCurrentPage(1)
                    }}
                  >
                    Limpiar búsqueda
                  </button>
                )}
              </div>
            ) : (
              paginatedCourses.map((course) => {
                const isStudentView = isStudent

                const studentStatus = isStudentView
                  ? {
                    activo: { text: "Activo", color: "green" },
                    completado: { text: "Completado", color: "success" },
                    retirado: { text: "Retirado", color: "red" },
                    suspendido: { text: "Suspendido", color: "orange" },
                  }[course.enrollmentStatus] || null
                  : null

                const realProgress = clamp(Number(getCourseProgress?.(course.id) ?? course.finalGrade ?? course.progress ?? 0))
                const tipoLower = String(course?.tipo || "").toLowerCase()
                const showExamBtn = tipoLower.includes("examen")

                const onEnterCourse = () => {
                  if (!isStudentView) return
                  enterCourseAndStoreFlags(course)
                }

                if (layout === "list") {
                  if (isStudentView) {
                    return (
                      <StudentListRow
                        key={course.id}
                        course={course}
                        realProgress={realProgress}
                        onEnterCourse={onEnterCourse}
                        studentStatus={studentStatus}
                        showExamBtn={showExamBtn}
                        isExamOpen={openExamId === course.id}
                        toggleExam={toggleExam}
                        examApi={{ fetchMyExamBookings, getCourseProgress }}
                      />
                    )
                  }

                  if (isAdmin) return <AdminListRow key={course.id} course={course} navigate={navigate} />
                  if (isTeacher) return <TeacherListRow key={course.id} course={course} navigate={navigate} />
                  return null
                }

                return (
                  <CourseCardGrid
                    key={course.id}
                    course={course}
                    navigate={navigate}
                    getCourseProgress={getCourseProgress}
                    examApi={{ fetchMyExamBookings, getCourseProgress }}
                    onEnterCourse={onEnterCourse}
                    studentStatus={studentStatus}
                    view={{ isAdmin, isTeacher, isStudent, roleLower }}
                  />
                )
              })
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                ← Anterior
              </button>

              <div className="pagination-info">
                <span>
                  Página {currentPage} de {totalPages}
                </span>
                <span className="total-courses">
                  ({filteredCourses.length} curso{filteredCourses.length !== 1 ? "s" : ""}
                  {searchTerm ? ` encontrado${filteredCourses.length !== 1 ? "s" : ""}` : " total"})
                </span>
              </div>

              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Siguiente →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal de introducción a la plataforma */}
      <PlatformIntroModal
        open={introOpen}
        accepted={termsAccepted}
        onToggleAccepted={setTermsAccepted}
        onContinue={acceptAndContinue}
        onLogout={() => {
          localStorage.clear()
          window.location.href = "/login"
        }}
        allowClose={false}
        // ✅ NUEVO: términos desde BD
        termsLoading={regTermsLoading}
        termsError={regTermsError}
        termsContent={regTermsData?.contenido || ""}
        termsVersion={regTermsData?.version || null}
      />

      {/* ✅ Tutorial de bienvenida */}
      <WelcomeTourModal
        open={showWelcomeTour}
        step={welcomeStep}
        storageKey={welcomeTourKey}
        onNext={() => {
          if (welcomeStep === 1) {
            // Último paso: cerrar y marcar como visto
            try {
              localStorage.setItem(welcomeTourKey, "1");
            } catch { }
            setShowWelcomeTour(false);
          } else {
            // Avanzar al siguiente paso
            setWelcomeStep(prev => prev + 1);
          }
        }}
        onSkip={() => {
          try {
            localStorage.setItem(welcomeTourKey, "1");
          } catch { }
          setShowWelcomeTour(false);
        }}
      />
    </div>
  )
}


